import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Search, Download, Inbox, TrendingUp, Users, FileSearch, Activity as ActivityIcon, UserPlus, Trash2, BookOpen } from "lucide-react";
import LeadDetail from "../components/admin/LeadDetail";
import AdminFunnel from "../components/admin/AdminFunnel";
import AdminTeam from "../components/admin/AdminTeam";
import AdminActivity from "../components/admin/AdminActivity";
import AdminBlog from "../components/admin/AdminBlog";
import LeadCard from "../components/admin/LeadCard";
import usePageMeta from "../lib/usePageMeta";

export default function Admin() {
  usePageMeta({ title: "Admin · AARA", noindex: true });
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!me.data.is_admin) {
          navigate("/admin/login?denied=1");
          return;
        }
        setUser(me.data);
        await reload();
      } catch {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const reload = async () => {
    const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
    setLeads(l.data);
    setStats(s.data);
  };

  const onLeadChanged = (updated) => {
    setLeads((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  };
  const onLeadDeleted = (id) => {
    setLeads((prev) => prev.filter((x) => x.id !== id));
    setSelectedLeadId(null);
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aara-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const sourceFiltered = filter === "all" ? leads : leads.filter((l) => l.source === filter);
  const filtered = !query.trim()
    ? sourceFiltered
    : sourceFiltered.filter((l) => {
        const q = query.toLowerCase();
        return (
          (l.name || "").toLowerCase().includes(q) ||
          (l.email || "").toLowerCase().includes(q) ||
          (l.phone || "").toLowerCase().includes(q) ||
          (l.property_address || "").toLowerCase().includes(q) ||
          (l.county || "").toLowerCase().includes(q) ||
          (l.state || "").toLowerCase().includes(q)
        );
      });

  const selectedLead = leads.find((l) => l.id === selectedLeadId) || null;

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F5F0]">
        <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-[#F7F5F0]/80 border-b border-[#E2DDD3]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DDD3] grid place-items-center overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/iyaa4h8h_American%20Asset%20logo-01.png" alt="AARA" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <p className="font-serif-display text-lg leading-none text-[#0F172A]">AARA Admin</p>
              <p className="text-xs text-[#64748B] mt-0.5">Lead Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#64748B] hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <Button onClick={logout} variant="outline" className="border-[#E2DDD3] text-[#0F172A] hover:bg-[#EFEAE0]" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 sm:py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard icon={<Inbox className="w-5 h-5" />} label="Total Leads" value={stats?.total ?? 0} />
          <StatCard icon={<TrendingUp className="w-5 h-5" />} label="New (Unworked)" value={stats?.new ?? 0} accent />
          <StatCard icon={<FileSearch className="w-5 h-5" />} label="Claim Estimates" value={stats?.claim_estimates ?? 0} />
          <StatCard icon={<Users className="w-5 h-5" />} label="Last 7 days" value={stats?.last_7_days ?? 0} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-[#EFEAE0] flex-wrap h-auto">
            <TabsTrigger value="leads" data-testid="tab-leads"><Inbox className="w-4 h-4 mr-2" />Leads</TabsTrigger>
            <TabsTrigger value="funnel" data-testid="tab-funnel"><TrendingUp className="w-4 h-4 mr-2" />Funnel</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog"><BookOpen className="w-4 h-4 mr-2" />Blog</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team"><UserPlus className="w-4 h-4 mr-2" />Team</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity"><ActivityIcon className="w-4 h-4 mr-2" />Activity</TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email, phone, address…"
                  className="pl-10 bg-white border-[#E2DDD3] h-11"
                  data-testid="leads-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="h-11 px-3 rounded-md border border-[#E2DDD3] bg-white text-sm"
                  data-testid="source-filter"
                >
                  <option value="all">All sources</option>
                  <option value="claim_estimate">Claim Estimates</option>
                  <option value="contact">Contact Messages</option>
                </select>
                <Button onClick={exportCsv} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="export-csv">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>

            <p className="text-xs text-[#64748B]">{filtered.length} lead{filtered.length !== 1 ? "s" : ""}{query.trim() ? ` matching "${query}"` : ""}</p>

            <div className="space-y-3" data-testid="leads-list">
              {filtered.length === 0 && (
                <div className="bg-white rounded-2xl border border-[#E2DDD3] p-12 text-center">
                  <Inbox className="w-10 h-10 mx-auto text-[#FF008F] mb-3" />
                  <p className="font-serif-display text-xl text-[#0F172A]">No leads found</p>
                  <p className="text-sm text-[#64748B] mt-1">{leads.length === 0 ? "New submissions from the website will appear here." : "Try a different search or filter."}</p>
                </div>
              )}
              {filtered.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  onClick={() => setSelectedLeadId(lead.id)}
                />
              ))}
            </div>
          </TabsContent>

          {/* Funnel Tab */}
          <TabsContent value="funnel">
            <AdminFunnel />
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <AdminBlog />
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <AdminTeam currentUserEmail={user?.email} />
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <AdminActivity />
          </TabsContent>
        </Tabs>
      </main>

      <LeadDetail
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLeadId(null)}
        onChanged={onLeadChanged}
        onDeleted={onLeadDeleted}
      />
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className={`bg-white rounded-2xl border border-[#E2DDD3] p-5 shadow-sm ${accent ? "ring-1 ring-[#FF008F]/30" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-[#64748B]">{label}</span>
        <span className={accent ? "text-[#FF008F]" : "text-[#1E3A8A]"}>{icon}</span>
      </div>
      <p className="mt-2 font-serif-display font-bold text-3xl text-[#0F172A]">{value}</p>
    </div>
  );
}
